// v2 (Attio-style) shell. Renders left-rail Sidebar + full-bleed Outlet
// for the current route. The legacy v1 shell still lives in App.tsx;
// this one is opt-in via localStorage('cc.ui') === 'v2' until phase 5
// when v2 becomes the default and v1 is removed.

import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { createApiClient, type ApiClient } from '../api.js';
import { apiUrl } from '../apiBase.js';
import { Avatar } from '../components/Avatar.js';
import { COLLEAGUES_SCOPES, getMyPhoto } from '../graph.js';
import { CommandPalette } from './CommandPalette.js';
import { MobileNav } from './MobileNav.js';
import { MobileTopbar } from './MobileTopbar.js';
import { SessionExpiredBanner } from './SessionExpiredBanner.js';
import { Sidebar } from './Sidebar.js';

interface HealthResponse {
  status: string;
  version?: string;
  env?: string;
}

export function AppShellV2() {
  const { state, getIdToken, getAccessToken } = useAuth();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // Cmd-K (Ctrl-K on non-Mac) toggles the command palette globally.
  // Stay out of inputs/textareas so users typing in the search box
  // don't accidentally swap to the palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isDemo = state.kind === 'authenticated' && state.user.demo;

  const api = useMemo<ApiClient>(
    () => createApiClient({ demo: isDemo, getIdToken }),
    [isDemo, getIdToken]
  );

  useEffect(() => {
    fetch(apiUrl('/api/health'))
      .then((r) => (r.ok ? (r.json() as Promise<HealthResponse>) : null))
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    if (state.kind !== 'authenticated' || state.user.demo) return;
    let cancelled = false;
    let revoke: string | null = null;
    (async () => {
      const token = await getAccessToken(COLLEAGUES_SCOPES);
      if (!token || cancelled) return;
      try {
        const url = await getMyPhoto(token);
        if (!cancelled && url) {
          setPhotoUrl(url);
          revoke = url;
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [state, getAccessToken]);

  if (state.kind !== 'authenticated') return null;
  const { user } = state;

  return (
    <div className="min-h-screen flex">
      {/* Desktop / wide-viewport sidebar. Hidden below md (768px) so
          the side panel and phones get the full width for content. */}
      <div className="hidden md:flex">
        <Sidebar
          workspaceLabel="Cloud Contacts"
          footer={
            <div className="flex items-center gap-2 px-2 py-2 text-xs">
              <Avatar name={user.name} photoUrl={photoUrl ?? undefined} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-strong font-medium truncate text-sm">{user.name}</p>
                {user.email && <p className="text-muted truncate">{user.email}</p>}
              </div>
            </div>
          }
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <MobileTopbar
          userName={user.name}
          email={user.email}
          photoUrl={photoUrl}
        />
        <main
          className="flex-1 px-3 md:px-6 py-3 md:py-6 pb-20 md:pb-3"
          key={location.pathname}
        >
          <Outlet context={{ api }} />
        </main>
        <footer className="hidden md:flex px-6 pb-4 text-xs text-muted items-center justify-between">
          <span>
            {health
              ? `API: ${health.status}${health.version ? ` · v${health.version}` : ''}${health.env ? ` · ${health.env}` : ''}`
              : 'API: unreachable'}
            {user.demo && <span className="ml-2 text-amber-600 dark:text-amber-300">demo mode</span>}
          </span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="text-faint hover:text-mid font-mono"
            title="Open command palette"
          >
            ⌘K
          </button>
        </footer>
      </div>
      <MobileNav />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <SessionExpiredBanner />
    </div>
  );
}
